import React, { useState } from 'react';
import { WPImage } from '@/data/wp/operations/queries/fragments/image';
import { WPVideo } from '@/data/wp/operations/queries/fragments/video';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Pagination } from 'swiper';
import Image from 'next/image';
import styled from '@mui/system/styled';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import theme from '@/mui/theme';
import Video from '@/components/ui/Video';

type VerticalSliderSlideImage = {
  mediatype: 'image',
  image: WPImage
}

type VerticalSliderSlideVideo = {
  mediatype: 'video',
  video: WPVideo
}

type VerticalSliderSlide = VerticalSliderSlideImage | VerticalSliderSlideVideo;

interface VerticalSliderProps {
  showDots?: boolean;
  backgroundDots?: boolean;
  slides: VerticalSliderSlide[];
}

const StyledSwiper = styled(Swiper)({
  background: '#182641',
  [theme.breakpoints.down('sm')]: {
    'height': '300px',
  },
  [theme.breakpoints.up('sm')]: {
    'height': '740px',
  },
  '&.hide-dots .swiper-pagination': {
    display: 'none'
  },
  '&.background-dots .swiper-pagination::after': {
    content: '""',
    background: '#000000',
    opacity: '59%',
    borderRadius: '9px',
    position: 'absolute',
    top: '-13px',
    left: '-16px',
    height: 'calc(100% + 34px)',
    width: '100%',
    padding: '22.5px',
    zIndex: '-1',
    [theme.breakpoints.down('sm')]: {
      top: '-6px',
      left: '-12px',
      height: 'calc(100% + 19px)',
      padding: '19px',
    },
  },
  '&.swiper-container-vertical > .swiper-pagination-bullets': {
    right: '40.5px',

    [theme.breakpoints.down('sm')]: {
      right: '30px',
    },

    '.swiper-pagination-bullet': {
      height: '14px',
      width: '14px',
      marginBottom: '17px',
      opacity: '0.26',
      backgroundColor: '#ffffff',
      '&:last-of-type': {
        marginBottom: '0'
      },
      '&.swiper-pagination-bullet-active': {
        opacity: '1'
      }
    }
  },
});

const StyledPaginationNumber = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  'span': {
    color: '#ffffff',
    fontSize: '18px',
    fontWeight: 'bold',
    letterSpacing: '0',
    lineHeight: '25px',
    marginLeft: '4px',
    '&.active-page-number': {
      fontSize: '46px',
      letterSpacing: '-0.58px',
      lineHeight: '40px',
      marginLeft: '0',
    }
  }
});

// install Swiper modules
SwiperCore.use([Pagination]);

const VerticalSlider: React.FC<VerticalSliderProps> = ({ slides, backgroundDots = true }) => {
  const showDots = slides.length > 1;
  const showDotsClass = !showDots ? 'hide-dots' : '';
  const backgroundDotsClass = backgroundDots ? 'background-dots' : '';
  const [activeIndexSlide, setActiveIndexSlide] = useState(1);
  return (<Box>
    <StyledSwiper
      className={`${showDotsClass} ${backgroundDotsClass}`}
      direction={'vertical'}
      pagination={{
        clickable: true,
      }}
      spaceBetween={0}
      slidesPerView={1}
      onSwiper={(swiper) => {
        setActiveIndexSlide(swiper.activeIndex + 1);
      }}
      onSlideChange={(swiper: Swiper) => {
        /* @ts-ignore */
        setActiveIndexSlide(swiper.activeIndex + 1);
      }}
    >
      {slides.map((slideContent, index) => (
        <SwiperSlide key={index}>
          {slideContent.mediatype === 'image' && slideContent.image &&
            <Image
              layout='fill'
              src={slideContent.image.mediaItemUrl}
              alt={slideContent.image.altText}
              objectFit='contain'
              objectPosition='center'
            />
          }
          {slideContent.mediatype === 'video' && slideContent.video &&
            <Box sx={{ width: { xs: '100%', lg: '1110px'} , left: '50%', position: 'absolute', top: '50%', transform: 'translate(-50%, -50%)' }}>
              <Video
                layout="responsive"
                media={slideContent.video}
              />
            </Box>
          }
        </SwiperSlide>
      ))}
      <Container className="pagination-container" sx={{ bottom: { sm: '69px', xs: '20px' }, top: 'unset', position: { sm: 'relative', xs: 'absolute' }, zIndex: '20' }}>
        <Grid container rowSpacing={0}>
          <Grid item xs={12}>
            <StyledPaginationNumber className="swiper-number-pagination">
              <span className="active-page-number">{activeIndexSlide}</span>
              <span>/</span>
              <span className="total-page-number">{slides.length}</span>
            </StyledPaginationNumber>
          </Grid>
        </Grid>
      </Container>
    </StyledSwiper>
  </Box>)
}

export default VerticalSlider;
